/**
 * Field Name Overrides Preview Component (Read-only)
 * Displays field name customizations for blueprints in view mode
 */

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import IconProvider from '@/components/ui/IconProvider';
import { ExportTemplate } from '@/types/exportTemplates';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
import { extractBlueprintFields } from './utils/fieldExtractors';

interface FieldNameOverridesPreviewProps {
  template: ExportTemplate;
  availableTemplates: any[];
}

export const FieldNameOverridesPreview = ({ template, availableTemplates }: FieldNameOverridesPreviewProps) => {
  const { t } = usePlatformTranslation();
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string>('');
  
  // Get the selected blueprint
  const selectedBlueprint = availableTemplates.find(bp => bp.id === selectedBlueprintId);
  
  // Get field name overrides for the selected blueprint
  const blueprintOverrides = template.fieldNameOverrides?.[selectedBlueprintId] || {};
  
  // Extract fields from the blueprint
  const blueprintFields = selectedBlueprint?.data ? extractBlueprintFields(selectedBlueprint.data) : [];
  
  return (
    <div>
      <Label className="text-sm font-semibold mb-2 block">Field Name Customization</Label>
      <p className="text-sm text-muted-foreground mb-3">
        Customize field names for specific blueprints in the database export. By default, fields use the naming convention, but you can override individual field names.
      </p>
      
      <div className="space-y-3">
        <div className="space-y-2">
          <Label className="text-sm">Select Blueprint</Label>
          <Select
            value={selectedBlueprintId}
            onValueChange={setSelectedBlueprintId}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a blueprint to view field overrides" />
            </SelectTrigger>
            <SelectContent>
              {availableTemplates.map(blueprint => (
                <SelectItem key={blueprint.id} value={blueprint.id}>
                  {blueprint.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {selectedBlueprintId && (
          <div className="border rounded overflow-hidden">
            {blueprintFields.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-accent">
                  <tr>
                    <th className="text-left p-2 font-medium">Field Name</th>
                    <th className="text-left p-2 font-medium">Export As</th>
                    <th className="text-center p-2 font-medium w-20">Use Custom</th>
                  </tr>
                </thead>
                <tbody>
                  {blueprintFields.map((fieldName) => {
                    const override = blueprintOverrides[fieldName];
                    return (
                      <tr key={fieldName} className="border-t">
                        <td className="p-2">{fieldName}</td>
                        <td className="p-2 font-mono text-xs">
                          {override?.exportName || <span className="text-muted-foreground italic">Default</span>}
                        </td>
                        <td className="p-2 text-center">
                          {override?.useExportName ? (
                            <IconProvider iconstring="fa-check" size={14} className="text-primary inline-block" />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No fields found in this blueprint
              </div>
            )}
          </div>
        )}
        
        {!selectedBlueprintId && (
          <div className="p-4 border border-dashed rounded text-center text-muted-foreground text-sm">
            Select a blueprint to view or edit field name customizations
          </div>
        )}
      </div>
    </div>
  );
};
